import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { FormControl, FormControlOptions, UntypedFormGroup, ValidatorFn } from '@angular/forms';
import { Subscription } from 'rxjs';
import { createStandardControlValidatorsForProperty } from '../../utils/validation-helpers';
import type { Property } from '../../../core/models/property.model';
import type { PropertyType } from '../../../core/models/property-type.model';
import type { EntityEditorPropertyChangedEvent } from '../../models/entity-editor-property-changed.event';

/**
 * Base component for entity property editors.
 * Handles form control initialization, updates, and validation.
 */
@Component({
  selector: 'app-entity-property-base',
  template: '',
  standalone: false
})
export class EntityPropertyBaseComponent<T> implements OnChanges, OnDestroy {
  @Input() property: Property;
  @Input() propertyType: PropertyType;
  @Input() entityId: string;
  @Input() entityForm: UntypedFormGroup;
  @Input() placeholder: string;
  @Output() changed = new EventEmitter<EntityEditorPropertyChangedEvent>();

  protected propertyForm: FormControl<T>;

  /**
   * Set to true when the control is focused. Focused controls will not be auto-updated if the property value changes
   * externally. This prevents the case where the user is editing a value when an update happens and the control would
   * be reset to the value from the entity.
   * @protected
   */
  protected focused = false;

  /**
   * Set to true when the form control for the property has been initialized. This is used to prevent re-initializing the
   * control when the property or entity form changes.
   */
  private propertyFormInitialized = false;

  /**
   * Subscription to the valueChanges of the form control.
   */
  private onChangeSubscription: Subscription;

  ngOnChanges(changes: SimpleChanges): void {
    const propertyChanged = changes['property'];
    const entityFormChanged = changes['entityForm'];

    // If the property or the entity form group changes, we will need to update the control.
    if (propertyChanged || entityFormChanged) {
      // If form exists but property form is not initialized, check if the entity form already contains a control for the
      // property key. If not, then create one and add it to the form. If it exists, just store a reference to it to this
      // component.
      if (this.entityForm && !this.propertyFormInitialized) {
        this.initializePropertyForm();
      }

      if (this.propertyForm) {
        this.updatePropertyForm(changes);
      }
    }
  }

  ngOnDestroy(): void {
    this.onChangeSubscription?.unsubscribe();
  }

  /**
   * Returns the options for the form control. Designed to be potentially overridden by subclasses.
   */
  protected getFormControlOptions(): FormControlOptions {
    return { updateOn: 'blur' };
  }

  /**
   * Returns the validators for the form control. Designed to be potentially overridden by subclasses.
   */
  protected getValidators(): ValidatorFn[] {
    if (!this.property) {
      return [];
    }

    return createStandardControlValidatorsForProperty(this.property);
  }

  /**
   * Creates a subscription to the valueChanges of the form control. Designed to be overridden by subclasses.
   */
  protected createOnChangeSubscription(): Subscription {
    return this.propertyForm.valueChanges.subscribe(newValue => {
      this.changed.emit({ propertyKey: this.propertyType.Key, value: newValue, measurementUnit: this.property.MeasurementUnit?.Key });
    });
  }

  private initializePropertyForm(): void {
    if (!this.entityForm.contains(this.propertyType.Key)) {
      this.propertyForm = new FormControl<T>(this.property.Value as T, this.getFormControlOptions());
      this.entityForm.addControl(this.propertyType.Key, this.propertyForm);
    } else {
      this.propertyForm = this.entityForm.get(this.propertyType.Key) as FormControl<T>;
    }
    // Subscribe to the valueChanges of the control, so we can emit events for updates.
    this.onChangeSubscription = this.createOnChangeSubscription();
    this.propertyFormInitialized = true;
  }

  /**
   * Updates the form control for the property based on the changes to the property or entity form group. The value only
   * gets updated if the property value has changed and the control is not focused.
   * If the value is changed, the setValue method will be called with emitModelToViewChange set to true to prevent the
   * control from emitting a value change event, causing an infinite loop.
   */
  private updatePropertyForm(changes: SimpleChanges): void {
    const defaultEventOptions = { emitEvent: false };

    // The validators for the form control may change based on the property, so we need to update them.
    this.propertyForm.setValidators(this.getValidators());

    const oldValue: Property = changes['property']?.previousValue;
    const newValue: Property = changes['property']?.currentValue;

    if (changes['property'].firstChange || (oldValue && oldValue?.Value !== newValue?.Value && !this.focused)) {
      this.propertyForm.setValue(this.property.Value as T, {
        ...defaultEventOptions,
        // Important! We only want to emit the viewChange-event in this case, because emitting the other events would
        // trigger a property update.
        emitModelToViewChange: true
      });
    }
    this.propertyForm.updateValueAndValidity(defaultEventOptions);

    // Update disabled state
    if (!this.property.Rules.EnableIf || this.property.Rules.ReadOnlyIf) {
      this.propertyForm.disable(defaultEventOptions);
    } else {
      this.propertyForm.enable(defaultEventOptions);
    }
  }
}
